import React, { useState, useCallback, useEffect } from 'react'
import { SafeAreaView } from 'react-native'
import { useRecoilState } from 'recoil'
import { useFocusEffect } from '@react-navigation/native'
import { ScrollView } from 'react-native-gesture-handler'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { GetTheme, GetThemeParams, GetUnits, SaveTheme, UploadFile } from '@/domain/usecases'
import { useErrorHandler, useImage, useTheme, useThemeClient } from '@/presentation/hooks'
import { Box, HStack, VStack, Text, UnitsSelector, Loading, Chip, OutdatedError } from '@/presentation/components'
import { ActionsButtons, ColorPicker, LoginBackgroundSelector, LogoSelector, ModalOnDiscardChanges, ModalOnSaveChanges, currentUnitState, isLoadingState, unitsState } from '@/presentation/pages/Theme/components'
import { styles } from './styles'

export type ThemeProps = {
  getTheme: GetTheme
  saveTheme: SaveTheme
  uploadFile: UploadFile
  getUnits: GetUnits
}

export const Theme: React.FC<ThemeProps> = ({
  getTheme,
  saveTheme,
  uploadFile,
  getUnits
}) => {
  const theme = useTheme()
  const handleError = useErrorHandler()
  const { getImage } = useImage()
  const { hasChanged, setThemeClient, getThemeClient } = useThemeClient()
  const [logoEmpresa, setLogoEmpresa] = useState('')
  const [backgroundEmpresa, setBackgroundEmpresa] = useState('')
  const [isLoading, setLoading] = useRecoilState(isLoadingState)
  const [currentUnit, setCurrentUnit] = useRecoilState(currentUnitState)
  const [units, setUnits] = useRecoilState(unitsState)

  const themeClient = getThemeClient()

  useFocusEffect(
    useCallback(() => {
      setLoading(true)

      getUnits
        .getAll()
        .then(async units => {
          const getThemeParams: GetThemeParams = {
            codigoUnidade: units[0].codigoUnidade
          }
          const theme = await getTheme.get(getThemeParams)
          setThemeClient(theme)

          void getImage(theme.imageLogo, 'logo')
            .then(logo => { setLogoEmpresa(logo) })
            .catch(console.error)
          void getImage(theme.bgLogin, 'login')
            .then(background => { setBackgroundEmpresa(background) })
            .catch(console.error)

          setCurrentUnit(units[0].codigoUnidade)
          setUnits(units)
        })
        .catch(handleError)
        .finally(() => setLoading(false))
    }, [])
  )

  useEffect(() => {
    setLoading(true)
    if (currentUnit) {
      void (async () => {
        const getConfigParams: GetThemeParams = {
          codigoUnidade: currentUnit
        }
        const theme = await getTheme.get(getConfigParams)
        setThemeClient(theme)

        void getImage(theme.imageLogo, 'logo')
          .then(logo => { setLogoEmpresa(logo) })
          .catch(console.error)
        void getImage(theme.bgLogin, 'login')
          .then(background => { setBackgroundEmpresa(background) })
          .catch(console.error)
      })().finally(() => { setLoading(false) })
    }
  }, [currentUnit])

  return (
    <SafeAreaView style={styles.container}>
      <OutdatedError />
      <ScrollView style={styles.scroll}>
        <HStack justify='center' items='center' style={{
          backgroundColor: `${themeClient.mainColor}CC`
        }}>
          <MaterialCommunityIcons size={24} color={theme.colors.white} name='brush-variant' />
          <Text variant="subtitle1" style={{ ...styles.headerText, color: theme.colors.white }} >
            Tema da Unidade
          </Text>
          <UnitsSelector
            disabled={isLoading}
            currentUnit={currentUnit}
            setCurrent={currentUnit => { setCurrentUnit(currentUnit) }}
            units={units}
          />
        </HStack>
      {isLoading
        ? <Loading text='Buscando Tema da Unidade'/>
        : <Box style={styles.content}>
          {['primeirotema', 'padrao'].includes(themeClient?.fonte) &&
            <HStack pv={12} wrap justify='center' spacing={24}>
              <Chip color={theme.colors.error} text='Tema Padrão' />
              <Text style={{ marginLeft: 12, fontWeight: '600', fontSize: 12 }}>Requer primeira gravação</Text>
            </HStack>
          }
            <HStack wrap spacing={24}>
              <ColorPicker label='Cor principal' name='mainColor' />
              <ColorPicker label='Cor dos botões de confirmação' name='buttonSave' />
            </HStack>
            <HStack wrap spacing={24}>
              <ColorPicker label='Cor do título da tela de login' name='loginColor' />
              <ColorPicker label='Cor dos ícones do menu superior' name='menuColor' />
            </HStack>
            <VStack m={16}>
              <LogoSelector fileName={logoEmpresa} />
              <LoginBackgroundSelector fileName={backgroundEmpresa} />
            </VStack>
          </Box>
      }
      </ScrollView>
      {(hasChanged() || ['primeirotema', 'padrao'].includes(themeClient?.fonte)) &&
        <ActionsButtons firstRecording={['primeirotema', 'padrao'].includes(themeClient?.fonte)} />
      }
      <ModalOnSaveChanges uploadFile={uploadFile} saveTheme={saveTheme} />
      <ModalOnDiscardChanges />
    </SafeAreaView>
  )
}
